/**
 * Вспомогательные функции синхронизации корзины с сервером.
 *
 * Сервер хранит корзину целиком и целиком же перезаписывает её при каждом сохранении. Поэтому:
 * - изменения, которые не удалось отправить, откладываются в черновик (localStorage), чтобы не пропасть;
 * - давно не сверявшаяся корзина перед отправкой сравнивается с серверной и при расхождении сливается с ней,
 *   иначе устаревшая копия (другая вкладка, другое устройство, менеджер) затёрла бы чужие изменения.
 */

export interface BasketLike {
    token?: string
    note?: string
    timestamp?: number
    ID?: number
    version?: number
    items: BasketItem[]
}

interface BasketItem {
    Артикул: string
    quantity: number | string
    selected?: boolean
}

/** состав корзины: артикул → количество */
export interface BasketSnapshot {
    [vCode: string]: number
}

export interface BasketDraft {
    basket: BasketLike
    /** серверная корзина, поверх которой сделаны изменения; null — неизвестна */
    base: BasketSnapshot | null
    /** ID этой серверной корзины */
    baseID?: number
    /** время последнего изменения */
    changedAt: number
}

const DRAFT_PREFIX = 'basketDraft:'
const DRAFT_TTL = 3 * 24 * 60 * 60 * 1000

const has = (obj: object, key: string) => Object.prototype.hasOwnProperty.call(obj, key)
//как в конструкторе Product: пустое количество считается единицей
const quantityOf = (item: BasketItem) => +item.quantity || 1

export function isBasketLike(data: any): data is BasketLike {
    return !!data && typeof data === 'object' && !Array.isArray(data) && Array.isArray(data.items)
}

export function basketSnapshot(basket: { items?: BasketItem[] }) {
    const snapshot: BasketSnapshot = {}
    ;(basket.items || []).forEach((item) => {
        snapshot[item.Артикул] = quantityOf(item)
    })
    return snapshot
}

export function sameSnapshot(a: BasketSnapshot | null, b: BasketSnapshot | null) {
    if (!a || !b) return false
    const keys = Object.keys(a)
    return keys.length === Object.keys(b).length && keys.every((key) => has(b, key) && a[key] === b[key])
}

/**
 * Трёхстороннее слияние позиций корзины
 * @param local позиции с несохранёнными локальными изменениями
 * @param remote позиции, которые сейчас на сервере
 * @param base состав корзины, от которого разошлись local и remote; без него слияние только добавляет позиции
 */
export function mergeBasketItems<T extends BasketItem>(local: T[], remote: T[], base: BasketSnapshot | null) {
    const remoteByCode: { [vCode: string]: T } = {}
    remote.forEach((item) => {
        remoteByCode[item.Артикул] = item
    })
    const localCodes: { [vCode: string]: boolean } = {}
    const merged: T[] = []

    local.forEach((item) => {
        const vCode = item.Артикул
        localCodes[vCode] = true
        const untouched = !!base && has(base, vCode) && base[vCode] === quantityOf(item)
        if (!has(remoteByCode, vCode)) {
            //нетронутую здесь позицию удалили на сервере, иначе она добавлена или изменена здесь
            if (!untouched) merged.push(item)
        } else {
            const remoteItem = remoteByCode[vCode]
            //количество меняли только на сервере
            merged.push(untouched && quantityOf(remoteItem) !== quantityOf(item) ? remoteItem : item)
        }
    })
    remote.forEach((item) => {
        //позиция из общей базы, которой здесь уже нет, удалена здесь, иначе она добавлена на сервере
        if (!has(localCodes, item.Артикул) && !(base && has(base, item.Артикул))) merged.push(item)
    })
    return merged
}

function parseDraft(json: string | null): BasketDraft | null {
    try {
        const draft: BasketDraft = JSON.parse(json || 'null')
        if (draft && isBasketLike(draft.basket) && Date.now() - draft.changedAt < DRAFT_TTL) return draft
    } catch (error) {
        console.error(error)
    }
    return null
}

export function readDraft(token: string): BasketDraft | null {
    try {
        const json = localStorage.getItem(DRAFT_PREFIX + token)
        if (!json) return null
        const draft = parseDraft(json)
        if (!draft) removeDraft(token)
        return draft
    } catch (error) {
        return null
    }
}

export function writeDraft(token: string, draft: BasketDraft) {
    try {
        localStorage.setItem(DRAFT_PREFIX + token, JSON.stringify(draft))
    } catch (error) {
        //localStorage переполнен: корзина остаётся только в памяти вкладки
        console.error(error)
    }
}

export function hasDraft(token: string) {
    try {
        return localStorage.getItem(DRAFT_PREFIX + token) !== null
    } catch (error) {
        return false
    }
}

export function removeDraft(token: string) {
    try {
        localStorage.removeItem(DRAFT_PREFIX + token)
    } catch (error) {
    }
}

/**
 * Черновики клиентов, к корзинам которых на этом устройстве больше не возвращались
 */
export function removeExpiredDrafts() {
    try {
        Object.keys(localStorage)
            .filter((key) => key.indexOf(DRAFT_PREFIX) === 0 && !parseDraft(localStorage.getItem(key)))
            .forEach((key) => localStorage.removeItem(key))
    } catch (error) {
    }
}
